
import { computed, defineComponent, ref } from 'vue'
import { IVacancy, LocalizationPath } from '@tokyojob/core'
import { CIcon, CIconFactory, CVacancyCardMedium, getLocalization, IconSize } from '@tokyojob/frontend-components'
import { JobGTagEventFactory, LocalizationService } from '@tokyojob/frontend-core'
import { PropType } from 'vue/types/v3-component-props'
import { getProvidedApp, getProvidedGtag, getRouter } from '~/base/utils/provided-getter'

/**
 * Move carousel left
 */
const leftArrowIcon = CIconFactory.Icon({
  icon: 'circle-arrow-left',
  size: IconSize.large
})
/**
 * Move carousel right
 */
const rightArrowIcon = CIconFactory.Icon({
  icon: 'circle-arrow-right',
  size: IconSize.large
})

export default defineComponent({
  components: {
    CIcon,
    CVacancyCardMedium
  },
  props: {
    jobs: {
      type: Array as PropType<IVacancy[]>,
      required: true
    }
  },
  setup(props, { expose }) {
    const router = getRouter()
    const localization = getLocalization(getProvidedApp())
    const recommendedJobsTitle = LocalizationService.t<string>(localization, LocalizationPath.recommendedJobsTitle)

    /**
     * Keep a ref of parent div to compare against cards inside it
     */
    const showRecommendedJobs = computed(() => {
      return props.jobs.length > 0
    })
    const recommendationsDiv = ref(null)

    /**
     * Array of refs will be used to track location of cards against parent div
     */
    const elements = ref([] as any)
    /**
     * Function to scroll to previous recommended vacancy
     */
    const scrollToNext = () => {
      for (const item of elements.value) {
        if (isDivStillAhead(item, recommendationsDiv.value!)) {
          return item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' })
        }
      }
    }
    /**
     * Function to scroll to next recommended vacancy
     */
    const scrollToPrevious = () => {
      for (let i = props.jobs.length - 1; i >= 0; i--) {
        const item = elements.value[i]
        if (isDivStillBehind(item, recommendationsDiv.value!))
          return item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
      }
    }
    /**
     * Function to check next card which is not fully visible in parent div
     */
    function isDivStillAhead(innerDiv: HTMLDivElement, outerDiv: HTMLDivElement) {
      const innerRect = innerDiv.getBoundingClientRect()
      const outerRect = outerDiv.getBoundingClientRect()
      // 1px is subtracted to check if card is still ahead of parent div. HACKY but works
      return innerRect.right - 1 > outerRect.right
    }
    /**
     * Function to check previous card which is not fully visible in parent div
     */
    const isDivStillBehind = (innerDiv: HTMLDivElement, outerDiv: HTMLDivElement) => {
      const innerRect = innerDiv.getBoundingClientRect()
      const outerRect = outerDiv.getBoundingClientRect()
      // 1px is added to check if card is still behind parent div. HACKY but works
      return innerRect.left + 1 < outerRect.left
    }
    expose({ isDivStillAhead })
    const gtag = getProvidedGtag()
    const methods = {
      onRecommendedJobClicked(job: IVacancy) {
        gtag.track(JobGTagEventFactory.recommendedJobOpened(job))
      }
    }
    return {
      showRecommendedJobs,
      elements,
      scrollToNext,
      scrollToPrevious,
      recommendationsDiv,
      leftArrowIcon,
      rightArrowIcon,
      router,
      recommendedJobsTitle,
      methods
    }
  }
})
